import React, {useEffect, useState} from "react";
import { Link } from "react-router-dom";
import LocalizedText from "../../presentational/LocalizedText";
import Input from "../../presentational/Input";
import Button from "../../presentational/Button";
import useTranslate from "../../../hooks/useTranslate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/pro-regular-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { useTurnstile } from "../../../hooks/useTurnstile";

interface KnownUserLoginParameters {
  serverError: string;
  isLoading: boolean;
  emailOrPhone: string;
  notYouLink: string;
  forgotPassword: () => void;
  finishLogin: (password: string) => void;
  setEmailOrPhone: (emailOrPhone: string) => void;
  setRememberMe: (rememberMe: boolean) => void;
}

const KnownUserLogin: React.FC<KnownUserLoginParameters> = ({
  serverError,
  isLoading,
  emailOrPhone,
  notYouLink,
  forgotPassword,
  finishLogin,
  setEmailOrPhone,
  setRememberMe,
}) => {
  const [password, setPassword] = useState('')
  const [unmaskPassword, setUnmaskPassword] = useState(false)
  const translations = useTranslate(["participant-login-password-label", "participant-login-password-placeholder", "participant-login-next-label",])
  useTurnstile();

  const handleChangeEmailOrPhone = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailOrPhone(e.target.value)
  }

  const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
  }

  const handleNotYouClick = () => {
    setEmailOrPhone('')
    setRememberMe(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    finishLogin(password)
    return false
  }

  return (
    <form className="login-action" onSubmit={handleSubmit}>
      <p className="personalized-welcome-back">
        <h3 className="personalized-welcome-back-title"><LocalizedText textKey="participant-login-login-password-title" /></h3>
        <span className="personalized-welcome-back-userid"> {emailOrPhone}</span>
        <Button variant="minimal" intent="primary" to={notYouLink} onClick={handleNotYouClick}><LocalizedText textKey="participant-login-not-you-label" /></Button>
      </p>
      {
        serverError && (
          <p className="server-error">{serverError}</p>
        )
      }
      <input type="text" id="username" value={emailOrPhone} onChange={handleChangeEmailOrPhone} onInput={handleChangeEmailOrPhone} autoComplete="username" hidden />
      <div className="field-group">
        <label htmlFor="password">{translations["participant-login-password-label"]}</label>
        <div className="combo-input-group">
          <Input
            autoComplete="current-password"
            className="autofocus"
            disabled={isLoading}
            id="password"
            type={unmaskPassword ? 'text' : 'password'}
            value={password}
            placeholder={translations["participant-login-password-placeholder"]}
            fullWidth={true}
            onChange={handleChangePassword}
            htmlAttributes={{
              onInput: handleChangePassword,
              spellCheck: false,
              autoCorrect: "off",
              autoCapitalize: "none",
              maxLength: 64,
            }}
          />
          <button
            type="button"
            onClick={() => setUnmaskPassword(!unmaskPassword)}
            className="password-mask-toggle"
            title="Toggle password visibility">
               <FontAwesomeIcon icon={unmaskPassword ? faEye as IconProp: faEyeSlash as IconProp} />
          </button>
        </div>
      </div>
      <div>
        <div id="cf-turnstile" className="cf-turnstile"></div>
        <Button
          className="primary-action"
          disabled={!password.length}
          onClick={handleSubmit}
          intent='primary'
          loading={isLoading}
          width='100%'
          type='submit'
        >
          <span dangerouslySetInnerHTML={{ __html: translations["participant-login-next-label"] }} />
        </Button>
      </div>
      <Button
        className="forgot-password-button"
        onClick={forgotPassword}
        width='100%'
        intent='secondary'
        variant='minimal'
      >
        <LocalizedText textKey='participant-login-forgot-password-label' />
      </Button>
    </form>
  )
}

export default KnownUserLogin